import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import { MessageDialog } from "@bryntum/schedulerpro";
import { duplicateBoardAndTestDrive } from "../../../network/network.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { shouldShowAsyncDialog } from "../../../bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup.jsx";

const DUPLICATE_OPTIONS = [
  { key: "all", label: "All", description: "This is a great option for experimenting and training." },
  { key: "template", label: "Template", description: "Includes all status, category, tags, teams, type, and phase settings; icons; and saved views. This is the best option for creating a board template." },
  { key: "tasks", label: "Tasks", description: "Includes task general data, files, notes, budgets, checklists, and linked contacts." },
  { key: "resources", label: "Resources", description: "Includes resource general data, files, notes, checklists, and linked contacts." },
  { key: "clients", label: "Clients", description: "Includes client general data, files, notes, checklists, and linked contacts." },
  { key: "users", label: "Users", description: "Includes all board users, roles, permissions, and linked contacts." },
  { key: "contacts", label: "Contacts", description: "Includes all board contacts." }
];

const widgetRef = {
  createButton: "createButton",
  closeButton: "closeButton",
  boardNameField: "boardNameField",
  duplicateOptionsCheckboxes: "duplicateOptionsContainer",
};

const DUPLICATE_CHECK_OPTIONS = {
  all: "all",
  template: "Template",
  tasks: "Tasks",
  resources: "Resources",
  clients: "Clients",
  users: "Users",
  contacts: "Contacts",
}

export class SwivuDuplicateBoardEditor extends EditorBase {
  static get $name() {
    return "SwivuDuplicateBoardEditor";
  }

  static get configurable() {
    return {
      minWidth: "40em",
      width: "50em",
      height: "50em",
      minHeight: "30em",
      maxHeight: "55em",
      closable: true,
      autoClose: false,
      autoShow: false,
      cls: "resize minimal-gradient-editor",
      centered: true,
      modal: true,
      draggable: true,
      closeAction: "destroy",
      overflow: "scroll",
      bodyCls: "!gap-0",
      autoUpdateRecord: true,
      items: {
        containerLayout: {
          type: "container",
          cls: "w-full h-full overflow-y-auto",
          items: {
            duplicateOptionsContainer: {
              type: "container",
              cls: "flex-col",
              items: DUPLICATE_OPTIONS.map((option) => ({
                type: "checkbox",
                name: option.key,
                ref: option.key,
                listeners: {
                  change: "up.onChangeCheckbox"
                },
                cls: "swivu-duplicate-checkbox-label",
                text:
                  `<strong>${option.label}</strong>` +
                  `<p class="ml-[30px]">${option.description}</p>  ${
                    option.label === 'All' ? '<br/><hr class="mt-0"/>' : ''
                  }`
              }))
            },
            boardNameField: {
              type: "text",
              name: "boardNameField",
              cls: "my-4",
              onInput: "up.onBoardNameChange"
            }
          }
        }
      },
      bbar: {
        cls: "dark:!bg-[#2c2a2e]",
        items: {
          closeButton: {
            type: "button",
            icon: "fa-light fa-close",
            text: "Cancel",
            cls: "bg-[transparent] text-[#3884e1] border-[1.5px] !rounded-md",
            onClick: "up.close"
          },
          createButton: {
            onClick: "up.onDuplicateBoard",
            cls: "bg-[transparent] text-[#228a0c] border-[1.5px] !rounded-md",
            text: "Duplicate",
            icon: "fa-sharp fa-thin fa-clapperboard",
            disabled: true
          }
        }
      }
    };
  }


  editRecord(record, context) {
    super.editRecord(record, context, { isFirstShow: true })
    this.context = context;
    this.record = record;
    this.onComplete = context.onComplete;
    this.title = `Duplicate ${record.name}`;
    this.changeCrossToEsc();

    const boardNameField = this.getWidgetByRef(widgetRef.boardNameField);
    boardNameField.placeholder = `Duplicating ${record.name}. Please enter a new name.`;
  }

  onChangeCheckbox({source, userAction}) {
    this.record[source.name] = source.value;
    if (userAction) {
      const widgetCheckbox = this.getWidgetByRef(widgetRef.duplicateOptionsCheckboxes);
      if (source.name === DUPLICATE_CHECK_OPTIONS.all.toLowerCase()) {
        widgetCheckbox.eachWidget((widget) => {
          widget.value = source.value;
        });
      } else {
        const everythingWidget = widgetCheckbox.getWidgetByRef(DUPLICATE_CHECK_OPTIONS.all.toLowerCase());
        const isAllDupOptionsChecked = Object.keys(DUPLICATE_CHECK_OPTIONS)
          .filter((dupOpt) => dupOpt !== DUPLICATE_CHECK_OPTIONS.all.toLowerCase())
          .every((dupOpt) => this.record[dupOpt]);
        everythingWidget.value = isAllDupOptionsChecked;
      }
    }
  }

  onBoardNameChange() {
    const createBtn = this.bbar?.getWidgetByRef(widgetRef.createButton);
    const boardNameField = this.getWidgetByRef(widgetRef.boardNameField);
    createBtn.disabled = !boardNameField.value.trim().length;
  }

  onDuplicateBoard() {
    const boardNameField = this.getWidgetByRef(widgetRef.boardNameField);
    const everythingCheck = DUPLICATE_CHECK_OPTIONS.all.toLowerCase();
    const boardDataCopyOptions = this.record[everythingCheck] ? ['everything']: Object.keys(this.record).reduce((accum, dupOpt) => {
      if (DUPLICATE_CHECK_OPTIONS[dupOpt] && this.record[dupOpt]) accum.push(dupOpt);
      return accum;
    }, []);
    const boardDuplicateFormData = {
      name: boardNameField.value,
      boardIdToCopy: this.record.id,
      boardDataCopyOptions,
    }


    // if duplicating board without selecting any copy options
    if (boardDuplicateFormData.boardIdToCopy && !boardDuplicateFormData.boardDataCopyOptions.length) {
      MessageDialog.alert({ title: "Duplicate Options Required", message: "At least one option must be selected when duplicating a board." });
      return false;
    }

    // Use reusable dialog utility
    const showDialog = shouldShowAsyncDialog('BOARD_DUPLICATION');
    showDialog && this.context.editorHandler.openEditor(EDITOR_KEYS.asyncTaskMessageEditor, null, {record: "BOARD_DUPLICATION"}, { useExisting: true, by: "recordType" });
    duplicateBoardAndTestDrive(boardDuplicateFormData.boardIdToCopy, boardDuplicateFormData.name, boardDataCopyOptions)
      .then(() => {
        this.onComplete && this.onComplete();
        this.hide();
      })
  }

  boardToAddStoreChanges(boardToAdd) {
    this.popupClosable = false;
    this.mask("Duplicating board...");
    const boardFormPopup = this;
    const boardManager = this.context.boardManager;
    const boardStore = this.context.boardStore;
    const detacher = boardStore.on({
      idChange() {
        boardFormPopup.unmask();
        boardFormPopup.hide();
        boardManager.changeSelectedBoard(boardToAdd);
        detacher();
      },
      swivuAddBoardFailed() {
        boardFormPopup.unmask();
        boardFormPopup.hide();
        detacher();
      },
      once: true
    });
  }
}

SwivuDuplicateBoardEditor.initClass();